import type { DatadogInitModel } from '@cw-elements/analytics-provider';
import { DefaultPrivacyLevel } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { ENV_VARIABLES } from '@/constants';

import pkg from '../../../package.json';

import {
  DATADOG_SERVICE,
  DATADOG_SITE,
  DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  DEFAULT_SESSION_SAMPLE_RATE
} from './constants';
import hasDatadogCredentials from './hasDatadogCredentials';
import type { DatadogSampleRates } from './types';

/**
 * Builds the Datadog RUM configuration, or `undefined` when credentials are
 * missing. `service`, `env` and `version` are mandatory in the analytics
 * provider — without them RUM is not initialized at all — so they are wired
 * from the build rather than hardcoded.
 */
const getDatadogRumConfig = ({
  sessionSampleRate = DEFAULT_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate = DEFAULT_SESSION_REPLAY_SAMPLE_RATE
}: DatadogSampleRates = {}): DatadogInitModel | undefined => {
  if (!hasDatadogCredentials()) {
    return undefined;
  }

  const allowedTracingUrls = [
    ENV_VARIABLES.PANORAMA_API_URL,
    ENV_VARIABLES.PANORAMA_V2_API_URL
  ].filter(Boolean);

  return {
    applicationId: ENV_VARIABLES.DATADOG_APP_ID,
    clientToken: ENV_VARIABLES.DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: ENV_VARIABLES.ENVIRONMENT,
    version: pkg.version,
    sessionSampleRate,
    sessionReplaySampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    compressIntakeRequests: true,
    allowedTracingUrls,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
    // `addReactError` (used by ErrorBoundary) queues its reports until the
    // React plugin's `onRumStart` hook fires, so without registering it here
    // every React error is silently swallowed instead of reaching RUM.
    // No `router` option on purpose — passing one flips the SDK to
    // `trackViewsManually` and we let it track views automatically.
    plugins: [reactPlugin()]
  };
};

export default getDatadogRumConfig;
